"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { site } from "@/lib/site";
import { attributionParams, CLICK_ID_PARAMS } from "@/lib/attribution";
import { hasAdvertisingConsent, onConsentChange } from "@/lib/consent";

// Carries attribution from the landing URL through to every link that points at
// the app, so a visitor who signs up stays attributable across the origin hop.
// Two kinds of param travel:
//
//   - `referral`, captured once and kept for the session in sessionStorage, so a
//     referral share survives navigation within the landing site.
//   - the ad-platform click ids and UTMs (see ATTRIBUTION_PARAMS), read from the
//     URL and falling back to the durable first-touch cookie.
//
// Stamped both proactively on mount and again at click time; the latter survives
// client re-renders that would otherwise reset the href back to the prerendered
// value. Patching the DOM after mount rather than building the href during render
// is deliberate: the pages are statically generated, so a render-time href would
// differ between the server and the client and trip a hydration mismatch.
const STORAGE_KEY = "tvt_referral";
const REFERRAL_PARAM = "referral";
const APP_HOST = new URL(site.appUrl).host;

function getReferral(): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get(REFERRAL_PARAM);
  if (fromUrl) {
    try {
      sessionStorage.setItem(STORAGE_KEY, fromUrl);
    } catch {}
    return fromUrl;
  }
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

// An explicit value already on the anchor always wins - the author meant it.
//
// The one exception is a click id we ourselves stamped and may no longer forward:
// no CTA is authored with a gclid on it, so any click id on an app-bound anchor
// came from here, and a visitor who withdraws advertising consent mid-session must
// not keep clicking links that still carry it.
function stamp(anchor: HTMLAnchorElement, params: Record<string, string>): void {
  let url: URL;
  try {
    url = new URL(anchor.href);
  } catch {
    return;
  }
  if (url.host !== APP_HOST) return;

  let changed = false;
  for (const key of CLICK_ID_PARAMS) {
    if (params[key] !== undefined || !url.searchParams.has(key)) continue;
    url.searchParams.delete(key);
    changed = true;
  }
  for (const [key, value] of Object.entries(params)) {
    if (url.searchParams.has(key)) continue;
    url.searchParams.set(key, value);
    changed = true;
  }
  if (changed) anchor.href = url.toString();
}

export function AttributionForwarder() {
  const pathname = usePathname();

  useEffect(() => {
    // Recomputed on every consent change as well as every route: granting
    // advertising has to start forwarding the remembered click id, and
    // withdrawing it has to stop, without waiting for a navigation.
    let params: Record<string, string> = {};

    const recompute = () => {
      params = attributionParams({ advertising: hasAdvertisingConsent() });
      const referral = getReferral();
      if (referral) params[REFERRAL_PARAM] = referral;

      // No early return on an empty set: with advertising withdrawn the pass has
      // nothing to add but may still have a click id to take back off.
      document
        .querySelectorAll<HTMLAnchorElement>("a[href]")
        .forEach((anchor) => stamp(anchor, params));
    };

    recompute();

    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.("a[href]");
      if (anchor) stamp(anchor as HTMLAnchorElement, params);
    };
    document.addEventListener("click", onClick, true);
    const unsubscribe = onConsentChange(recompute);
    return () => {
      document.removeEventListener("click", onClick, true);
      unsubscribe();
    };
  }, [pathname]);

  return null;
}
